import { type ReactNode, useEffect, useState } from 'react'

import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import { AlertTriangle, Check, Info, X } from '../icon/solar-icons.js'
import './portal-toast.css'

// ── Types ──

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
    id: number
    message: string
    type: ToastType
}

interface PortalToastContainerProps {
    lang: Language
    toasts: ToastItem[]
    onDismiss: (id: number) => void
}

// ── Icons ──

const TOAST_ICONS: Record<ToastType, ReactNode> = {
    success: <Check size={16} />,
    error: <X size={16} />,
    info: <Info size={16} />,
    warning: <AlertTriangle size={16} />,
}

// ── Toast item ──

const PortalToastItem = ({
    lang,
    toast,
    onDismiss,
}: {
    lang: Language
    toast: ToastItem
    onDismiss: (id: number) => void
}) => {
    const [isExiting, setIsExiting] = useState(false)

    useEffect(() => {
        const autoDismissMs = toast.type === 'error' ? 6000 : 3000
        const timer = setTimeout(() => {
            setIsExiting(true)
            setTimeout(() => onDismiss(toast.id), 250)
        }, autoDismissMs)
        return () => clearTimeout(timer)
    }, [toast.id, toast.type, onDismiss])

    const handleDismiss = () => {
        setIsExiting(true)
        setTimeout(() => onDismiss(toast.id), 250)
    }

    return (
        <div
            className={`portal-toast portal-toast--${toast.type}${isExiting ? ' is-exiting' : ''}`}
            role="alert"
        >
            <span className="portal-toast__icon">{TOAST_ICONS[toast.type]}</span>
            <span className="portal-toast__message">{toast.message}</span>
            <button
                type="button"
                className="portal-toast__close"
                onClick={handleDismiss}
                aria-label={t.common.close(lang)}
            >
                <X size={14} />
            </button>
        </div>
    )
}

// ── Container ──

export const PortalToastContainer = ({ lang, toasts, onDismiss }: PortalToastContainerProps) => {
    if (toasts.length === 0) return null

    return (
        <div className="portal-toast-container" aria-live="polite">
            {toasts.map((toast) => (
                <PortalToastItem key={toast.id} lang={lang} toast={toast} onDismiss={onDismiss} />
            ))}
        </div>
    )
}

// ── Hook-like helpers for managing toast state ──

let _nextId = 1

export const createToast = (message: string, type: ToastType = 'info'): ToastItem => ({
    id: _nextId++,
    message,
    type,
})
